/**
 * 品牌管理状态管理Hook
 * 职责: 管理品牌列表的加载、CRUD操作
 * 限制: <100行
 */

import { useState } from 'react';
import { App } from 'antd';
import * as brandService from '../../services/brandService';
import type { Brand, BrandListItem } from '../../services/brandService';

export const useBrandManagement = () => {
  const { message } = App.useApp();
  const [brands, setBrands] = useState<BrandListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);

  /**
   * 加载品牌列表
   */
  const loadBrands = async () => {
    setLoading(true);
    try {
      const data = await brandService.getAllBrands();
      if (Array.isArray(data)) {
        setBrands(data);
        if (data.length === 0) {
          console.log('品牌列表为空，数据库可能没有数据');
        }
      } else {
        console.error('API返回的数据不是数组:', data);
        setBrands([]);
        message.error('数据格式错误，请检查后端API响应');
      }
    } catch (error: any) {
      console.error('加载品牌列表失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      setBrands([]);
      const errorMsg = error.response?.data?.detail 
        || error.response?.data?.error 
        || error.userMessage 
        || error.message 
        || '加载品牌列表失败，请检查后端服务是否正常运行';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 打开新建对话框
   */
  const handleAdd = () => {
    setEditingBrand(null);
    setModalVisible(true);
  };

  /**
   * 打开编辑对话框
   */
  const handleEdit = async (record: BrandListItem) => {
    try {
      const brandDetail = await brandService.getBrandDetail(record.id);
      setEditingBrand(brandDetail);
      setModalVisible(true);
    } catch (error) {
      message.error('加载品牌详情失败');
    }
  };

  /**
   * 删除品牌
   */
  const handleDelete = async (id: number) => {
    try {
      await brandService.deleteBrand(id);
      message.success('删除成功');
      loadBrands();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  /**
   * 切换激活状态
   */
  const handleToggleActive = async (id: number) => {
    try {
      await brandService.toggleBrandActive(id);
      message.success('状态已更新');
      loadBrands();
    } catch (error) {
      message.error('更新失败');
    }
  };

  /**
   * 提交表单
   */
  const handleSubmit = async (values: any) => {
    try {
      if (editingBrand?.id) {
        await brandService.updateBrand(editingBrand.id, values);
        message.success('更新成功');
      } else {
        await brandService.createBrand(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadBrands();
      return true;
    } catch (error) {
      message.error('保存失败');
      return false;
    }
  };

  return {
    brands,
    loading,
    modalVisible,
    editingBrand,
    setModalVisible,
    loadBrands,
    handleAdd,
    handleEdit,
    handleDelete,
    handleToggleActive,
    handleSubmit,
  };
};

